import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingCart, Plus, Edit, Trash2, ArrowLeft, Search, MoreHorizontal, Calendar,
  DollarSign, Loader2, Check, X, Clock, Package, Users, FileText, CreditCard, RefreshCw,
  Building2, Banknote, Wallet, Settings, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import { format, parseISO } from "date-fns";

export default function SalesPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterClientId, setFilterClientId] = useState(searchParams.get("client_id") || "all");
  const [showDialog, setShowDialog] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Add Payment Dialog State
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentSale, setPaymentSale] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: "",
    payment_method: "",
    notes: "",
    generate_invoice: true,
    create_transaction: true
  });
  
  // Change Status Dialog State
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [statusSale, setStatusSale] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  
  const [formData, setFormData] = useState({
    client_id: "",
    product_service_id: "",
    quantity: 1,
    unit_price: "",
    discount_percent: 0,
    tax_percent: 0,
    currency: "USD",
    sale_date: new Date().toISOString().split('T')[0],
    status: "pending",
    payment_method: "",
    invoice_number: "",
    notes: "",
    initial_payment_amount: 0,
    auto_generate_invoice: false,
    create_transaction: true
  });

  // Default sale statuses (fallback) - these are workflow statuses, not payment methods
  const defaultSaleStatuses = [
    { id: "pending", name: "Pending", color: "yellow", is_final: false },
    { id: "partial_payment", name: "Partial Payment", color: "blue", is_final: false },
    { id: "completed", name: "Completed", color: "green", is_final: true },
    { id: "cancelled", name: "Cancelled", color: "gray", is_final: true },
    { id: "refunded", name: "Refunded", color: "red", is_final: true }
  ];

  // Payment methods - NO defaults, must be configured by user in Settings
  const paymentMethods = companySettings?.payment_methods?.filter(pm => pm.enabled) || [];
  const hasPaymentMethods = paymentMethods.length > 0;
  const saleStatuses = companySettings?.sale_statuses || defaultSaleStatuses;

  // Group payment methods by type for enhanced dropdowns
  const groupedPaymentMethods = {
    gateway: paymentMethods.filter(pm => pm.type === 'gateway'),
    bank: paymentMethods.filter(pm => pm.type === 'bank'),
    cash: paymentMethods.filter(pm => pm.type === 'cash'),
    check: paymentMethods.filter(pm => pm.type === 'check'),
    other: paymentMethods.filter(pm => !pm.type || pm.type === 'other')
  };

  // Get payment method icon based on type
  const getPaymentMethodIcon = (type) => {
    switch(type) {
      case 'gateway': return <CreditCard className="h-4 w-4 text-purple-500" />;
      case 'bank': return <Building2 className="h-4 w-4 text-blue-500" />;
      case 'cash': return <Banknote className="h-4 w-4 text-green-500" />;
      case 'check': return <FileText className="h-4 w-4 text-slate-500" />;
      default: return <Wallet className="h-4 w-4 text-amber-500" />;
    }
  };

  // Format payment method display name
  const formatPaymentMethodName = (method) => {
    if (method.type === 'bank' && method.bank_name) {
      const lastFour = method.account_number_last4 ? ` ****${method.account_number_last4}` : '';
      return `${method.name} (${method.bank_name}${lastFour})`;
    }
    return method.name;
  };

  // Render grouped payment method select content
  const renderPaymentMethodOptions = () => {
    // If no payment methods configured, show message
    if (!hasPaymentMethods) {
      return (
        <SelectItem value="__none__" disabled>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Settings className="h-4 w-4" />
            <span>No payment methods configured - Go to Settings</span>
          </div>
        </SelectItem>
      );
    }

    const hasGroups = Object.values(groupedPaymentMethods).some(group => group.length > 0);
    
    if (!hasGroups) {
      return paymentMethods.map((method) => (
        <SelectItem key={method.id} value={method.id}>
          <div className="flex items-center gap-2">
            {getPaymentMethodIcon(method.type)}
            <span>{formatPaymentMethodName(method)}</span>
          </div>
        </SelectItem>
      ));
    }

    return (
      <>
        {groupedPaymentMethods.gateway.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-xs text-muted-foreground">Payment Gateways</SelectLabel>
            {groupedPaymentMethods.gateway.map((method) => (
              <SelectItem key={method.id} value={method.id}>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-purple-500" />
                  <span>{method.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {groupedPaymentMethods.bank.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-xs text-muted-foreground">Bank Accounts</SelectLabel>
            {groupedPaymentMethods.bank.map((method) => (
              <SelectItem key={method.id} value={method.id}>
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-blue-500" />
                  <span>{formatPaymentMethodName(method)}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {groupedPaymentMethods.cash.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-xs text-muted-foreground">Cash</SelectLabel>
            {groupedPaymentMethods.cash.map((method) => (
              <SelectItem key={method.id} value={method.id}>
                <div className="flex items-center gap-2">
                  <Banknote className="h-4 w-4 text-green-500" />
                  <span>{method.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {groupedPaymentMethods.check.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-xs text-muted-foreground">Check</SelectLabel>
            {groupedPaymentMethods.check.map((method) => (
              <SelectItem key={method.id} value={method.id}>
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-500" />
                  <span>{method.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {groupedPaymentMethods.other.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-xs text-muted-foreground">Other</SelectLabel>
            {groupedPaymentMethods.other.map((method) => (
              <SelectItem key={method.id} value={method.id}>
                <div className="flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-amber-500" />
                  <span>{method.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
      </>
    );
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [salesRes, summaryRes, clientsRes, productsRes, settingsRes] = await Promise.all([
        api.get("/sales"),
        api.get("/sales/summary"),
        api.get("/clients"),
        api.get("/products-services"),
        api.get("/company-settings").catch(() => ({ data: null }))
      ]);
      setSales(salesRes.data);
      setSummary(summaryRes.data);
      setClients(clientsRes.data);
      setProducts(productsRes.data);
      if (settingsRes.data) {
        setCompanySettings(settingsRes.data);
      }
    } catch (error) {
      console.error("Error fetching sales:", error);
      if (error.response?.status === 403) {
        toast.error("This feature is only available for business accounts");
        navigate("/dashboard");
      } else {
        toast.error(t("failed_load_data", "dashboard") || "Failed to load data");
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (searchParams.get("action") === "new") {
      openCreateDialog();
    }
  }, [searchParams]);

  const openCreateDialog = () => {
    setEditingSale(null);
    setFormData({
      client_id: filterClientId !== "all" ? filterClientId : "",
      product_service_id: "",
      quantity: 1,
      unit_price: "",
      discount_percent: 0,
      tax_percent: 0,
      currency: "USD",
      sale_date: new Date().toISOString().split('T')[0],
      status: "pending",
      payment_method: "",
      invoice_number: "",
      notes: "",
      initial_payment_amount: 0,
      auto_generate_invoice: false,
      create_transaction: true
    });
    setShowDialog(true);
  };

  const openEditDialog = (sale) => {
    setEditingSale(sale);
    setFormData({
      client_id: sale.client_id,
      product_service_id: sale.product_service_id,
      quantity: sale.quantity,
      unit_price: sale.unit_price.toString(),
      discount_percent: sale.discount_percent,
      tax_percent: sale.tax_percent,
      currency: sale.currency,
      sale_date: sale.sale_date?.split('T')[0] || "",
      status: sale.status,
      payment_method: sale.payment_method || "",
      invoice_number: sale.invoice_number || "",
      notes: sale.notes || "",
      initial_payment_amount: 0,
      auto_generate_invoice: sale.auto_generate_invoice || false,
      create_transaction: false
    });
    setShowDialog(true);
  };

  const openPaymentDialog = (sale) => {
    setPaymentSale(sale);
    setPaymentData({
      amount: sale.amount_pending?.toString() || "",
      payment_method: sale.payment_method || "",
      notes: "",
      generate_invoice: true,
      create_transaction: true
    });
    setShowPaymentDialog(true);
  };

  const openStatusDialog = (sale) => {
    setStatusSale(sale);
    setNewStatus(sale.status);
    setShowStatusDialog(true);
  };

  const handleProductChange = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setFormData({
        ...formData,
        product_service_id: productId,
        unit_price: product.base_price.toString(),
        currency: product.currency
      });
    } else {
      setFormData({...formData, product_service_id: productId});
    }
  };

  const calculateTotal = () => {
    const subtotal = (parseFloat(formData.quantity) || 0) * (parseFloat(formData.unit_price) || 0);
    const discount = subtotal * ((parseFloat(formData.discount_percent) || 0) / 100);
    const tax = (subtotal - discount) * ((parseFloat(formData.tax_percent) || 0) / 100);
    return subtotal - discount + tax;
  };

  const handleSave = async () => {
    if (!formData.client_id) {
      toast.error("Please select a client");
      return;
    }
    if (!formData.product_service_id) {
      toast.error("Please select a product or service");
      return;
    }
    if (!formData.unit_price || parseFloat(formData.unit_price) <= 0) {
      toast.error("Please enter a valid price");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        quantity: parseFloat(formData.quantity),
        unit_price: parseFloat(formData.unit_price),
        discount_percent: parseFloat(formData.discount_percent) || 0,
        tax_percent: parseFloat(formData.tax_percent) || 0,
        initial_payment_amount: parseFloat(formData.initial_payment_amount) || 0,
        sale_date: new Date(formData.sale_date).toISOString()
      };

      if (editingSale) {
        await api.put(`/sales/${editingSale.id}`, payload);
        toast.success(t("sale_updated", "company") || "Sale updated successfully");
      } else {
        await api.post("/sales", payload);
        toast.success(t("sale_created", "company") || "Sale recorded successfully");
      }
      
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddPayment = async () => {
    if (!paymentSale) return;
    
    const amount = parseFloat(paymentData.amount);
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (amount > (paymentSale.amount_pending || 0)) {
      toast.error(`Amount cannot exceed pending balance of ${formatCurrency(paymentSale.amount_pending)}`);
      return;
    }

    setIsSaving(true);
    try {
      await api.post(`/sales/${paymentSale.id}/payments`, {
        amount,
        payment_method: paymentData.payment_method || null,
        notes: paymentData.notes || null,
        generate_invoice: paymentData.generate_invoice,
        create_transaction: paymentData.create_transaction
      });
      toast.success("Payment added successfully");
      setShowPaymentDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add payment");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangeStatus = async () => {
    if (!statusSale || !newStatus) return;

    setIsSaving(true);
    try {
      await api.put(`/sales/${statusSale.id}/status?new_status=${newStatus}`);
      toast.success(`Status changed to ${newStatus}`);
      setShowStatusDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change status");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/sales/${deleteConfirm.id}`);
      toast.success(t("sale_deleted", "company") || "Sale deleted successfully");
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const handleGenerateInvoice = async (sale) => {
    try {
      const response = await api.post(`/invoices/from-sales?due_days=30`, [sale.id]);
      toast.success(
        t("invoice_generated", "company") || `Invoice ${response.data.invoice_number} generated successfully`
      );
      navigate("/invoices");
    } catch (error) {
      console.error("Error generating invoice:", error);
      toast.error(error.response?.data?.detail || "Failed to generate invoice");
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusConfig = (status) => {
    const statusDef = saleStatuses.find(s => s.id === status);
    const colorMap = {
      green: { variant: "default", icon: Check },
      yellow: { variant: "outline", icon: Clock },
      blue: { variant: "secondary", icon: CreditCard },
      gray: { variant: "secondary", icon: X },
      red: { variant: "destructive", icon: X }
    };
    const config = colorMap[statusDef?.color] || colorMap.gray;
    return {
      label: statusDef?.name || status,
      variant: config.variant,
      icon: config.icon
    };
  };

  const filteredSales = sales.filter(sale => {
    const matchesSearch = 
      (sale.client_name && sale.client_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (sale.product_service_name && sale.product_service_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (sale.invoice_number && sale.invoice_number.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = filterStatus === "all" || sale.status === filterStatus;
    const matchesClient = filterClientId === "all" || sale.client_id === filterClientId;
    return matchesSearch && matchesStatus && matchesClient;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="max-w-[1200px] mx-auto px-4 py-4">
            <Skeleton className="h-8 w-48" />
          </div>
        </header>
        <main className="max-w-[1200px] mx-auto px-4 py-6">
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="sales-page">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-[1200px] mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold text-lg flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                {t("sales", "company") || "Sales"}
              </h1>
              <p className="text-xs text-muted-foreground">{sales.length} {t("total", "common") || "total"}</p>
            </div>
          </div>
          
          <Button onClick={openCreateDialog} data-testid="new-sale-button">
            <Plus className="h-4 w-4 mr-2" />
            {t("new_sale", "company") || "New Sale"}
          </Button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 py-6 space-y-4">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{t("total_revenue", "company") || "Total Revenue"}</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.total_revenue)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{t("this_month", "dashboard") || "This Month"}</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.month_revenue)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{t("total_sales", "company") || "Total Sales"}</p>
                <p className="text-2xl font-bold">{summary.total_sales_count}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{t("avg_sale_value", "company") || "Avg. Sale"}</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.average_sale_value)}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("search_sales", "company") || "Search sales..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_statuses", "company") || "All Statuses"}</SelectItem>
              {saleStatuses.map((status) => (
                <SelectItem key={status.id} value={status.id}>{status.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterClientId} onValueChange={setFilterClientId}>
            <SelectTrigger className="w-[180px]" data-testid="client-filter">
              <SelectValue placeholder={t("all_clients", "company") || "All Clients"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_clients", "company") || "All Clients"}</SelectItem>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Sales Table */}
        <Card>
          <CardContent className="p-0">
            {filteredSales.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery ? (t("no_sales_found", "company") || "No sales match your search") : (t("no_sales_yet", "company") || "No sales recorded yet")}
                </p>
                {!searchQuery && (
                  <Button className="mt-4" onClick={openCreateDialog}>
                    <Plus className="h-4 w-4 mr-2" />
                    {t("record_first_sale", "company") || "Record Your First Sale"}
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("date", "common") || "Date"}</TableHead>
                    <TableHead>{t("client", "company") || "Client"}</TableHead>
                    <TableHead>{t("product_service", "company") || "Product/Service"}</TableHead>
                    <TableHead>{t("total", "common") || "Total"}</TableHead>
                    <TableHead>{t("collected", "company") || "Collected"}</TableHead>
                    <TableHead>{t("pending", "common") || "Pending"}</TableHead>
                    <TableHead>{t("status", "common") || "Status"}</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSales.map((sale) => {
                    const statusConfig = getStatusConfig(sale.status);
                    const StatusIcon = statusConfig.icon;
                    const paymentProgress = sale.total_amount > 0 
                      ? ((sale.amount_collected || 0) / sale.total_amount) * 100 
                      : 0;
                    return (
                      <TableRow key={sale.id} data-testid={`sale-row-${sale.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">
                              {sale.sale_date ? format(parseISO(sale.sale_date), "MMM d, yyyy") : "-"}
                            </p>
                            {sale.invoice_number && (
                              <p className="text-xs text-muted-foreground">#{sale.invoice_number}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            {sale.client_name || "-"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-muted-foreground" />
                            {sale.product_service_name || "-"}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(sale.total_amount, sale.currency)}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-green-600 font-medium">
                              {formatCurrency(sale.amount_collected || 0, sale.currency)}
                            </p>
                            {sale.total_amount > 0 && (
                              <Progress value={paymentProgress} className="h-1 mt-1 w-16" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className={`font-medium ${(sale.amount_pending || 0) > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                            {formatCurrency(sale.amount_pending || 0, sale.currency)}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusConfig.variant} className="gap-1">
                            <StatusIcon className="h-3 w-3" />
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`sale-menu-${sale.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(sale)}>
                                <Edit className="h-4 w-4 mr-2" />
                                {t("edit", "common") || "Edit"}
                              </DropdownMenuItem>
                              {(sale.status === 'pending' || sale.status === 'partial_payment') && (
                                <DropdownMenuItem onClick={() => openPaymentDialog(sale)}>
                                  <CreditCard className="h-4 w-4 mr-2" />
                                  {t("add_payment", "company") || "Add Payment"}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => openStatusDialog(sale)}>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                {t("change_status", "company") || "Change Status"}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleGenerateInvoice(sale)}>
                                <FileText className="h-4 w-4 mr-2" />
                                {t("generate_invoice", "company") || "Generate Invoice"}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-destructive" 
                                onClick={() => setDeleteConfirm(sale)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                {t("delete", "common") || "Delete"}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sale-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingSale ? (t("edit_sale", "company") || "Edit Sale") : (t("new_sale", "company") || "New Sale")}
            </DialogTitle>
            <DialogDescription>
              {editingSale ? (t("edit_sale_desc", "company") || "Update sale details") : (t("new_sale_desc", "company") || "Record a new sale")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("client", "company") || "Client"} *</Label>
                <Select value={formData.client_id} onValueChange={(v) => setFormData({...formData, client_id: v})}>
                  <SelectTrigger data-testid="sale-client-select">
                    <SelectValue placeholder={t("select_client", "company") || "Select client"} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("product_service", "company") || "Product/Service"} *</Label>
                <Select value={formData.product_service_id} onValueChange={handleProductChange}>
                  <SelectTrigger data-testid="sale-product-select">
                    <SelectValue placeholder={t("select_product", "company") || "Select product"} />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} - {formatCurrency(product.base_price, product.currency)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t("quantity", "company") || "Quantity"}</Label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                  data-testid="sale-quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("unit_price", "company") || "Unit Price"} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.unit_price}
                  onChange={(e) => setFormData({...formData, unit_price: e.target.value})}
                  data-testid="sale-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("currency", "common") || "Currency"}</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({...formData, currency: v})}>
                  <SelectTrigger data-testid="sale-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("discount_percent", "company") || "Discount %"}</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.discount_percent}
                  onChange={(e) => setFormData({...formData, discount_percent: e.target.value})}
                  data-testid="sale-discount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("tax_percent", "company") || "Tax %"}</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.tax_percent}
                  onChange={(e) => setFormData({...formData, tax_percent: e.target.value})}
                  data-testid="sale-tax-input"
                />
              </div>
            </div>
            
            {/* Total Preview */}
            <Card className="bg-muted/50">
              <CardContent className="p-4 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t("total_amount", "company") || "Total Amount"}</span>
                <span className="text-lg font-bold">{formatCurrency(calculateTotal(), formData.currency)}</span>
              </CardContent>
            </Card>
            
            {/* Initial Payment Amount (for partial payments) */}
            {!editingSale && (
              <div className="space-y-2">
                <Label>{t("initial_payment", "company") || "Amount Collected"}</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  max={calculateTotal()}
                  value={formData.initial_payment_amount}
                  onChange={(e) => setFormData({...formData, initial_payment_amount: e.target.value})}
                  placeholder="0.00"
                  data-testid="sale-initial-payment-input"
                />
                <p className="text-xs text-muted-foreground">
                  {t("partial_payment_hint", "company") || "Leave at 0 for pending payment, or enter amount received"}
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("sale_date", "company") || "Sale Date"}</Label>
                <Input
                  type="date"
                  value={formData.sale_date}
                  onChange={(e) => setFormData({...formData, sale_date: e.target.value})}
                  data-testid="sale-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("payment_method", "company") || "Payment Method"}</Label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData({...formData, payment_method: v})}>
                  <SelectTrigger data-testid="sale-payment-select">
                    <SelectValue placeholder={t("select_method", "company") || "Select method"} />
                  </SelectTrigger>
                  <SelectContent>
                    {renderPaymentMethodOptions()}
                  </SelectContent>
                </Select>
                {!hasPaymentMethods && (
                  <Alert className="mt-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {t("no_payment_methods_configured", "company") || "No payment methods configured."} <Link to="/settings" className="underline font-medium">{t("go_to_settings", "company") || "Go to Settings"}</Link> {t("to_add_payment_methods", "company") || "to add payment methods."}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t("notes", "common") || "Notes"}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder={t("sale_notes_placeholder", "company") || "Add notes (optional)"}
                rows={2}
                data-testid="sale-notes-input"
              />
            </div>
            
            {/* Auto Generate Invoice Toggle */}
            {!editingSale && (
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label>{t("auto_generate_invoice", "company") || "Auto Generate Invoice"}</Label>
                  <p className="text-xs text-muted-foreground">{t("auto_invoice_hint", "company") || "Automatically create invoice for this sale"}</p>
                </div>
                <Switch
                  checked={formData.auto_generate_invoice}
                  onCheckedChange={(checked) => setFormData({...formData, auto_generate_invoice: checked})}
                  data-testid="sale-auto-invoice-switch"
                />
              </div>
            )}
            
            {!editingSale && (
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label>{t("create_income_transaction", "company") || "Create Income Transaction"}</Label>
                  <p className="text-xs text-muted-foreground">{t("auto_track_income", "company") || "Automatically track this as income"}</p>
                </div>
                <Switch
                  checked={formData.create_transaction}
                  onCheckedChange={(checked) => setFormData({...formData, create_transaction: checked})}
                  data-testid="sale-create-transaction-switch"
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={isSaving}>
              {t("cancel", "common") || "Cancel"}
            </Button>
            <Button onClick={handleSave} disabled={isSaving} data-testid="save-sale-button">
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingSale ? (t("update", "common") || "Update") : (t("record_sale", "company") || "Record Sale")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-md" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>{t("add_payment", "company") || "Add Payment"}</DialogTitle>
            <DialogDescription>
              {t("add_payment_desc", "company") || "Record a payment for this sale"}
            </DialogDescription>
          </DialogHeader>
          
          {paymentSale && (
            <div className="space-y-4 py-4">
              {/* Payment Summary */}
              <Card className="bg-muted/50">
                <CardContent className="p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">{t("total_amount", "company") || "Total"}</span>
                    <span className="font-medium">{formatCurrency(paymentSale.total_amount, paymentSale.currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">{t("collected", "company") || "Collected"}</span>
                    <span className="font-medium text-green-600">{formatCurrency(paymentSale.amount_collected || 0, paymentSale.currency)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-sm font-medium">{t("pending", "common") || "Pending"}</span>
                    <span className="font-bold text-amber-600">{formatCurrency(paymentSale.amount_pending || 0, paymentSale.currency)}</span>
                  </div>
                </CardContent>
              </Card>
              
              <div className="space-y-2">
                <Label>{t("payment_amount", "company") || "Payment Amount"} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={paymentSale.amount_pending || 0}
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                  data-testid="payment-amount-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t("payment_method", "company") || "Payment Method"}</Label>
                <Select value={paymentData.payment_method} onValueChange={(v) => setPaymentData({...paymentData, payment_method: v})}>
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue placeholder={t("select_method", "company") || "Select method"} />
                  </SelectTrigger>
                  <SelectContent>
                    {renderPaymentMethodOptions()}
                  </SelectContent>
                </Select>
                {!hasPaymentMethods && (
                  <Alert className="mt-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {t("no_payment_methods_configured", "company") || "No payment methods configured."} <Link to="/settings" className="underline font-medium">{t("go_to_settings", "company") || "Go to Settings"}</Link> {t("to_add_payment_methods", "company") || "to add payment methods."}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>{t("notes", "common") || "Notes"}</Label>
                <Textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
                  placeholder={t("payment_notes_placeholder", "company") || "Add notes (optional)"}
                  rows={2}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label>{t("generate_invoice", "company") || "Generate Invoice"}</Label>
                  <p className="text-xs text-muted-foreground">{t("invoice_for_payment", "company") || "Create invoice for this payment"}</p>
                </div>
                <Switch
                  checked={paymentData.generate_invoice}
                  onCheckedChange={(checked) => setPaymentData({...paymentData, generate_invoice: checked})}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <Label>{t("create_transaction", "company") || "Record Transaction"}</Label>
                  <p className="text-xs text-muted-foreground">{t("transaction_for_payment", "company") || "Add to financial records"}</p>
                </div>
                <Switch
                  checked={paymentData.create_transaction}
                  onCheckedChange={(checked) => setPaymentData({...paymentData, create_transaction: checked})}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)} disabled={isSaving}>
              {t("cancel", "common") || "Cancel"}
            </Button>
            <Button onClick={handleAddPayment} disabled={isSaving} data-testid="confirm-payment-button">
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("add_payment", "company") || "Add Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="sm:max-w-sm" data-testid="status-dialog">
          <DialogHeader>
            <DialogTitle>{t("change_status", "company") || "Change Status"}</DialogTitle>
            <DialogDescription>
              {t("change_status_desc", "company") || "Update the status of this sale"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="new-status-select">
                <SelectValue placeholder={t("select_status", "company") || "Select status"} />
              </SelectTrigger>
              <SelectContent>
                {saleStatuses.map((status) => (
                  <SelectItem key={status.id} value={status.id}>{status.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)} disabled={isSaving}>
              {t("cancel", "common") || "Cancel"}
            </Button>
            <Button onClick={handleChangeStatus} disabled={isSaving} data-testid="confirm-status-button">
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("update", "common") || "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete", "common") || "Confirm Delete"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("delete_sale_warning", "company") || "Are you sure you want to delete this sale?"} {t("action_irreversible", "common") || "This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel", "common") || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("delete", "common") || "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
